// Order service for submitting checkout orders
import {fetchWithErrorHandling, guestPrefix, isAuthenticated} from './apiUtils';
import {BillingAddressFormData} from '../types';

export interface OrderSubmissionData {
  guestEmail?: string;
  recipientName?: string;
  recipientPhone?: string;
  savePhone?: boolean;
  paymentType?: string;
  paymentToken?: string;
  cardholderFirstName?: string;
  cardholderLastName?: string;
  billingAddress?: BillingAddressFormData;
  achFirstName?: string;
  achLastName?: string;
  achRoutingNumber?: string;
  achAccountNumber?: string;
  achAccountType?: string;
  shippingAddress?: {
    location?: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    postalcode?: string;
    countrycode?: string;
    countryName?: string;
    residential?: boolean;
  };
  comments?: string;
  shippingLocationId?: number;
  shippingServiceType?: string;
  shippingChargePrice?: number;
  specialDeliveryInstructions?: {
    deliveryStartDate: string;
    deliveryEndDate: string;
    eventStartDate: string;
    eventEndDate: string;
    shipmentContent: string;
  };
  checkoutFieldValues?: Record<string, string>;
  accountingCodeId?: number;
}

export interface OrderResult {
  orderNo: string;
  status: string;
  amount: number;
  receiptUrl?: string;
}

export interface OrderSubmissionResponse {
  success: boolean;
  masterOrderNo: string;
  orders: OrderResult[];
}

export class OrderService {
  /**
   * Submit an order.
   * Uses POST /api/v2/orders (authenticated) or POST /api/v2/guest/orders (guest).
   */
  static async submitOrder(data: OrderSubmissionData): Promise<OrderSubmissionResponse> {
    const endpoint = `/api/v2${guestPrefix()}/orders`;

    // Build request body
    const body: Record<string, unknown> = {
      recipientName: data.recipientName,
      recipientPhone: data.recipientPhone,
      savePhone: data.savePhone ?? false,
      paymentType: data.paymentType,
      paymentToken: data.paymentToken,
      cardholderFirstName: data.cardholderFirstName,
      cardholderLastName: data.cardholderLastName,
      billingAddress: data.billingAddress,
      achFirstName: data.achFirstName,
      achLastName: data.achLastName,
      achRoutingNumber: data.achRoutingNumber,
      achAccountNumber: data.achAccountNumber,
      achAccountType: data.achAccountType,
      comments: data.comments,
      shippingLocationId: data.shippingLocationId,
      shippingServiceType: data.shippingServiceType,
      shippingChargePrice: data.shippingChargePrice,
      specialDeliveryInstructions: data.specialDeliveryInstructions,
      checkoutFieldValues: data.checkoutFieldValues,
      accountingCodeId: data.accountingCodeId,
    };

    // Include guest-specific fields
    if (!isAuthenticated()) {
      if (data.guestEmail) {
        body.guestEmail = data.guestEmail;
      }
    }

    // Include new shipping address when provided (both guest and authenticated users)
    if (data.shippingAddress) {
      body.shippingAddress = data.shippingAddress;
    }

    return await fetchWithErrorHandling<OrderSubmissionResponse>(
        endpoint,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        },
        {handle422Errors: true}
    );
  }
}
